import { useState } from 'react'
import { Settings as SettingsIcon } from 'lucide-react'
import { EmptyState, Spinner } from '@bb/ui'
import { de } from '../../i18n'
import { EditSettingDialog } from './EditSettingDialog'
import { SettingsTable } from './SettingsTable'
import { useAdminSettings } from './useSettings'
import type { Setting } from './types'

/** §8: the settings view. `listSettings` (apps/api's settings/service.ts)
    already reduces every named setting to what is currently in effect, in
    name order, so this page is a fetch, a table (task 7's own idiom) and
    an edit dialog — no client-side sort, filter or search, matching the
    small, fixed set the seed migration (§8.3) creates. */
export function SettingsPage() {
  const settings = useAdminSettings()
  const [editingName, setEditingName] = useState<string | null>(null)

  const rows = settings.data?.settings ?? []
  const editing = rows.find((setting) => setting.name === editingName) ?? null

  function openEditor(setting: Setting) {
    setEditingName(setting.name)
  }

  return (
    <div className="p-6">
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-ink">{de.settings.title}</h1>
        <p className="mt-1 max-w-2xl text-xs text-ink-muted">{de.settings.intro}</p>
      </header>

      {settings.isPending ? (
        <div className="flex justify-center py-24"><Spinner className="size-6" /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={SettingsIcon} title={de.settings.empty.title} description={de.settings.empty.description} />
      ) : (
        <SettingsTable settings={rows} onEdit={openEditor} />
      )}

      <EditSettingDialog setting={editing} onOpenChange={(open) => !open && setEditingName(null)} />
    </div>
  )
}
